import { Suspense, useEffect } from "react";
// Every page is lazy-loaded; a canary rollout can leave a page asking for a
// chunk its pod does not have. lazyWithReload retries, then reloads once.
import { lazyWithReload as lazy } from "@/lib/lazy-with-reload";
import { legacyCycleTarget, legacyWorkflowSlugTarget } from "@/lib/run-routes";
import { Boxes, ListChecks, TerminalSquare} from "lucide-react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { AuthGuard, defaultLandingPath } from "./components/auth-guard";
import { AdminGuard } from "./components/admin-guard";
import BrandLoader from "./components/BrandLoader";
import { SuperAdminGuard } from "./components/super-admin-guard";
// New web-first revamp shell + pages (2026-05-22). Mounted at /app/*
// here; physically served at xp.io/go/app/* during P0-P3 via the
// VITE_ROUTER_BASE_PATH=/go build (see /proj/infra/compose/lumid_ui_go/).
// app-revamp /app/* shell retired in the S7 cutover — all /app/* routes are
// now <Navigate> redirects, so its lazy page imports (home/marketplace/loops/
// results/knowledge) are unreferenced and were removed 2026-06-19. (Some of
// those re-exported the moved pages/dashboard/* files, which would otherwise
// drag the deprecated files back into the build graph.) UserLayout likewise
// has no route.
const OnboardingWelcome = lazy(() => import("./pages/onboarding/welcome"));
const OnboardingDomain  = lazy(() => import("./pages/onboarding/domain"));
const OnboardingReady   = lazy(() => import("./pages/onboarding/ready"));
// /go-composer — Phase A1's xp.io/go composer. Lives in lumid_ui so
// session cookie auth flows naturally. Reachable at /go-composer from
// any bundle; the lumid-ui-go bundle (basename=/go) also serves it at
// "/" (see the Route definition below).
const Go            = lazy(() => import("./pages/Go"));

// Lumid Studio (Phase S1) — unified workspace shell. Lives at /studio/*
// alongside existing /app/* and /dashboard/* per the studio-plan.md
// decision: build alongside, no immediate cutover.
const StudioShell      = lazy(() => import("./components/StudioShell"));
const StudioChatHome   = lazy(() => import("./pages/studio/chat"));
const StudioIntents    = lazy(() => import("./pages/studio/intents"));
// Phase S5+ — real inbox (no longer a placeholder).
const StudioInbox      = lazy(() => import("./pages/studio/inbox"));
// Library page removed — the marketplace lives on xp.io. Old in-app
// paths bounce there via XpioRedirect.
function XpioRedirect() {
  useEffect(() => { window.location.replace("https://xp.io"); }, []);
  return null;
}
// Phase S3-C — app editor (lean v1).
const StudioApps         = lazy(() => import("./pages/studio/apps"));
const StudioWorkspace    = lazy(() => import("./pages/studio/StudioWorkspace"));
// App-defined UI surface (runtime-loaded markdown / native escape-hatch) at /studio/a/:app
const AppSurface         = lazy(() => import("./components/app-surface/AppSurface"));
// In-Studio markdown editor for an installed app's surface at /studio/a/:app/edit
const AppSurfaceEditor   = lazy(() => import("./components/app-surface/AppSurfaceEditor"));
// In-Studio YAML config editor for an installed app at /studio/a/:app/config
const AppManagePanel    = lazy(() => import("./components/app-surface/AppManagePanel"));
const AppConfigEditor    = lazy(() => import("./components/app-surface/AppConfigEditor"));
const AppPromptsEditor   = lazy(() => import("./components/app-surface/AppPromptsEditor"));
// W1 workflow surfaces folded into /studio/apps (per-app observability
// panel); /workflows and /mind redirect there, /workflows/:slug
// param-redirects into the owning app's panel. Runs stay as the
// cross-app run index.
const StudioRuns         = lazy(() => import("./pages/studio/runs"));
// The caller's own LQT strategies + their runtime cycles, over /lqt/inspect/*.
const StudioStrategies   = lazy(() => import("./pages/studio/strategies"));
// Workstream E — skills as a first-class surface (inventory + health + discovery).
const StudioSkills       = lazy(() => import("./pages/studio/skills"));
// Workstream F — cross-app experiments aggregate.
const StudioExperiments  = lazy(() => import("./pages/studio/experiments"));
const StudioRunDetail    = lazy(() => import("./pages/studio/run-detail"));
// Phase S3-D — per-agent knowledge browser (at /studio/knowledge/:agent).
const StudioKnowledge  = lazy(() => import("./pages/studio/knowledge"));
// "You, encoded" ledger at /studio/knowledge (distinct from the per-agent browser).
const StudioKnowledgeEncoded = lazy(() => import("./pages/studio/knowledge-encoded"));
// T13 — Generic intent-detail panel at /studio/intents/:intentId.
// Dispatches by intent.detail.body.kind (autoresearch | judgment | …).
const StudioIntentDetail = lazy(() => import("./pages/studio/intent-detail"));
// Phase S1.5 — Settings consolidation; Phase S4 — Admin tabs.
const StudioSettings   = lazy(() => import("./pages/studio/settings"));
const StudioAdmin      = lazy(() => import("./pages/studio/admin"));
// Claude Code quota status across all org accounts (super_admin only) — /code.
const StudioClaudeQuota = lazy(() => import("./pages/studio/claude-quota"));
const StudioClaudeSessions = lazy(() => import("./pages/studio/claude-sessions"));
// Phase S3-B — cycle inspector.
// "How Lumid works" — walkable 3-stage loop (Assemble → Adapt → Compound)
// illustrated against the demo intents. Stages 1-2 concrete, 3 open.
const StudioDocs       = lazy(() => import("./pages/studio/docs"));
const StudioMarketplace = lazy(() => import("./pages/studio/library"));
const StudioLibraryTabs = lazy(() => import("./pages/studio/library-tabs"));
const StudioData        = lazy(() => import("./pages/studio/data"));
const StudioRepo = lazy(() => import("./pages/studio/repo"));
const PublicShell = lazy(() => import("./components/PublicShell"));

// Auto-quant operator page (/dashboard/auto-quant/*)
const AutoQuantPage = lazy(() => import("./pages/app/auto-quant/index"));
const AutoQuantStrategyDetail = lazy(() => import("./pages/app/auto-quant/strategy-detail"));

// Lazy-load so first paint on /auth/login doesn't fetch the dashboard code.
const Login = lazy(() => import("./pages/login/login").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("./pages/login/register").then((m) => ({ default: m.Register })));
const Callback = lazy(() => import("./pages/auth/callback").then((m) => ({ default: m.AuthCallback })));
const ForgotPassword = lazy(() => import("./pages/auth/forgot-password"));
const ResetPassword = lazy(() => import("./pages/auth/reset-password"));
const XpioAutoresearchDoc = lazy(() => import("./pages/docs/xpio-autoresearch"));
// Public Quant Research Quickstart — linked from the logged-out landing.
const QuantQuickstartDoc = lazy(() => import("./pages/docs/quant-quickstart"));
// Logged-out landing at "/" (GOALS.md §6.14). Logged-in visitors never load it.
const Landing = lazy(() => import("./pages/landing/landing"));
const PluginImageCdDoc = lazy(() => import("./pages/docs/plugin-image-cd"));
const LqtStrategiesDoc = lazy(() => import("./pages/docs/lqt-strategies"));
const OperationsDoc = lazy(() => import("./pages/docs/operations"));
const ClaudePoolDoc = lazy(() => import("./pages/docs/claude-pool"));
const RedeemInvite = lazy(() => import("./pages/auth/redeem-invite"));

// The unified shell for /dashboard/* (absorbed the old /app/* tree in
// the 2026-04-24 merge). DashboardLayout + Overview are deprecated —
// AppLayout is the single shell now and /dashboard's index route shows
// the Apps landing.
const Profile = lazy(() => import("./pages/account/profile"));
const Tokens = lazy(() => import("./pages/account/tokens"));
const ClaudePoolManage = lazy(() => import("./pages/account/claude-pool"));
const FindataSQL = lazy(() => import("./pages/account/findata-sql"));
const ConnectGoogle = lazy(() => import("./pages/account/connect-google"));
const ConnectPowerAutomate = lazy(() => import("./pages/account/connect-power-automate"));
const ConnectMicrosoft = lazy(() => import("./pages/account/connect-microsoft"));
const Inbox = lazy(() => import("./pages/account/inbox"));
const SkillsNew = lazy(() => import("./pages/account/skills/new"));
const MemoryNew = lazy(() => import("./pages/account/memory/new"));
// Connect (OAuth account linking) dropped from the sidebar 2026-04-24;
// page file kept on disk at /pages/account/connect.tsx but no longer
// routed. Re-add import + route + tab entry if/when OAuth linking is
// needed again.
const AdminInvitations = lazy(() => import("./pages/account/admin-invitations"));
// AdminOverview — operational snapshot landing at /dashboard/admin/.
// Replaces the Runmesh-ported AdminDashboard (revenue/success metrics
// that are zero in our deployment); pulls live cluster/node/worker/
// user/audit data instead. Source at pages/dashboard/overview.tsx.
const AdminOverview = lazy(() => import("./pages/dashboard/overview"));
// Per-app user insights — how one xpio app is actually being used, across every
// tenant. Generic over apps: the app is a route param, so a second app is a URL
// rather than a new page.
const AppInsights = lazy(() => import("./pages/dashboard/app-insights"));
// Super-admin single pane of glass — billing/identity/QA/infra/build
// tiles + embedded Grafana panels. Lives at /dashboard/super-admin.
const SuperAdminDashboard = lazy(() => import("./pages/dashboard/super-admin"));
// Live operations status page — super_admin-gated. Reads the whole-stack
// stack_check scorecard + venue-health + resource-usage from the LQT
// gateway. Lives at /status/operations.
const OperationsStatusPage = lazy(() => import("./pages/status/operations"));
// Quant* + datasets-* page mounts retired 2026-06-19 — the /dashboard
// quant/datasets routes were already <Navigate> redirects into Studio;
// the page files moved to pages/deprecated/dashboard/. Imports removed.
// Datasets pages are now the Data Exploration apps — the dashboard routes
// redirect into /studio/a/lumid-data-*, and the components load via the
// app-surface native-registry (not these lazy consts). Removed as dead code.
// Quant competition leaf components retired 2026-06-09 — the lumid-market
// competition section is now PURE config surfaces (AppSurface + ui/*.md).
// The QuantCompetitions/Detail/StrategyDetail/Research pages were removed as
// route mounts; their irreducibly-interactive parts survive as lumid:native
// embeds (quantarena/surface-embeds.tsx → native-registry.ts).
// RunmeshUsers removed 2026-04-24 — canonical user admin now at
// /app/admin/users (backed by lumid_identity.users). sys_user stays as
// a lazy mirror for FK integrity but is no longer separately editable.
// RunmeshNodes retired 2026-04-24 — replaced by /app/admin/clusters.
// The Runmesh sys_gpu_node table still mirrors node rows for billing,
// but it's no longer edited through this UI.
const RunmeshSuppliers = lazy(() =>
  import("./runmesh/pages/SupplierManagement").then((m) => ({ default: m.SupplierManagement })),
);
const RunmeshSupplierNodes = lazy(() =>
  import("./runmesh/pages/SupplierNodeConfig").then((m) => ({ default: m.SupplierNodeConfig })),
);
const RunmeshBilling = lazy(() =>
  import("./runmesh/pages/BillingManagement").then((m) => ({ default: m.BillingManagement })),
);
const RunmeshWorkflowReview = lazy(() => import("./runmesh/pages/WorkflowReview"));

// Research surface (xp.io marketplace plan 2026-05-18) — LoopsPage /
// MarketplacePage / KnowledgePage / ResultsPage page mounts retired
// 2026-06-19; their /dashboard routes were already redirects. The page
// files moved to pages/deprecated/dashboard/. Imports removed.

// AppShell deprecated by Phase S7 cutover — /app/* now redirects to
// /studio/*; the focused Research shell is gone. Import + module
// retained on disk in case any deep-import path still references it;
// safe to delete once tree-shake confirms zero uses.

// Product surface — /app/*. Separate shell from /dashboard/*.
const AppLayout = lazy(() => import("./components/app-layout"));
const AppApps = lazy(() => import("./pages/app/apps"));
const AppWorkflows = lazy(() => import("./pages/app/workflows"));
// AppJobs (pages/dashboard/jobs) retired 2026-06-19 — /dashboard/jobs is
// now a redirect to /studio/apps; the page file moved to deprecated/.
const AppBilling = lazy(() => import("./pages/app/billing"));
const AppWorkflowDetail = lazy(() => import("./pages/app/workflow-detail"));
const AppWorkflowYaml = lazy(() => import("./pages/app/workflow-yaml"));
// AppProfile (Runmesh user profile) retired 2026-04-24 — the canonical
// Profile tab at /dashboard/profile renders the identity-side Profile
// component from /pages/account/profile.tsx.
const AppSchedules = lazy(() => import("./pages/app/schedules"));
const AppApiDocs = lazy(() => import("./pages/app/api-docs"));
// lumid-gpu-rentals is now PURE config surfaces (AppSurface + ui/*.md). The
// list/wizard route mounts were retired; the rental detail (terminal/SSH/logs/
// billing) survives as a lumid:native embed loaded via native-registry.ts.
// Replaced 2026-04-24 — Runmesh Submit + Lumilake Submit are now real
// "pick an existing workflow + configure params + submit" pages, not
// reuses of the list/management view. The old AppApps (UserDashboard)
// reverts to being the Workflow Builder at /dashboard.
const AppRunmeshSubmit = lazy(() => import("./pages/app/runmesh-submit"));
// T-UI-003 — LQT (Lumid QuantTrading) pages
const AppLqtTrader = lazy(() => import("@/lqt/pages/trader").then(m => ({ default: m.TraderPage })));
const AppLqtAuditor = lazy(() => import("@/lqt/pages/auditor").then(m => ({ default: m.AuditorPage })));
const AppLqtResearcher = lazy(() => import("@/lqt/pages/researcher").then(m => ({ default: m.ResearcherPage })));
const AppLqtOperator = lazy(() => import("@/lqt/pages/operator").then(m => ({ default: m.OperatorPage })));
const AppLqtAccountant = lazy(() => import("@/lqt/pages/accountant").then(m => ({ default: m.AccountantPage })));
const AppLqtAdmin = lazy(() => import("@/lqt/pages/admin").then(m => ({ default: m.AdminPage })));

// Lumilake workers page retired 2026-04-24 — /app/admin/lumilake-workers
// redirects to /app/admin/cluster-workers?role=lumilake. The unified
// Workers page pulls from the lumid_cluster registry (both roles).
// Canonical user admin at lum.id/app/admin/users — the one user store.
// Replaces /app/admin/users (Runmesh sys_user) and /app/admin/lumilake-users.
const AppAdminUsers = lazy(() => import("./pages/app/admin-users"));
const AppAdminUserDetail = lazy(() => import("./pages/app/admin-user-detail"));
const AppAdminUsersMatrix = lazy(() => import("./pages/app/admin-users-matrix"));
const AppAdminAudit = lazy(() => import("./pages/app/admin-audit"));
const AppAdminSetup = lazy(() => import("./pages/app/admin-setup"));
// lum.id/fm dashboard — live federation (sites/nodes/workers/jobs/vast).
// Reads mesh-federator directly via src/api/fm.ts, NOT the lumid_cluster mirror
// that the legacy registry screens (now unlisted, still routed) still use.
// Compute is the only admin section with a docked chat, so it wraps
// AdminSectionLayout rather than every section growing a rail it does not want.
const ComputeSection = lazy(() => import("./admin/fm/compute-section"));
const FmFleetRoute = lazy(() =>
  import("./admin/fm/routes").then((m) => ({ default: m.FleetRoute })),
);
// Through routes.tsx like Fleet and Jobs, not the bare tab: the tab needs to know
// the caller's role to decide which sites to fan out to. It is still NOT gated —
// home is readable by every signed-in user.
const FmSandboxesRoute = lazy(() =>
  import("./admin/fm/routes").then((m) => ({ default: m.SandboxesRoute })),
);
const FmJobsRoute = lazy(() =>
  import("./admin/fm/routes").then((m) => ({ default: m.JobsRoute })),
);

// lumid_cluster admin — /app/admin/clusters/*
const AppAdminClusters = lazy(() => import("./pages/app/admin-clusters"));
const AppAdminClustersNew = lazy(() => import("./pages/app/admin-clusters-new"));
const AppAdminClustersDetail = lazy(() => import("./pages/app/admin-clusters-detail"));
const ClusterDetailOverview = lazy(() => import("./admin/clusters/overview-tab"));
const ClusterDetailServers = lazy(() =>
  import("./admin/clusters/detail-routes").then((m) => ({ default: m.ServersRoute })),
);
const ClusterDetailNodes = lazy(() =>
  import("./admin/clusters/detail-routes").then((m) => ({ default: m.NodesRoute })),
);
const ClusterDetailWorkers = lazy(() =>
  import("./admin/clusters/detail-routes").then((m) => ({ default: m.WorkersRoute })),
);
const ClusterDetailCommercial = lazy(() =>
  import("./admin/clusters/detail-routes").then((m) => ({ default: m.CommercialRoute })),
);
const ClusterDetailSubmit = lazy(() =>
  import("./admin/clusters/detail-routes").then((m) => ({ default: m.SubmitRoute })),
);
const AppAdminClusterWorkers = lazy(() => import("./pages/app/admin-cluster-workers"));
const AppAdminInfrastructureSetup = lazy(() => import("./pages/app/admin/infrastructure-setup"));
// QuantArena admin pages — bridged via /api/v1/qa-admin/* nginx proxy
const AppAdminCompetitions = lazy(() => import("./pages/app/admin-competitions"));
const AppAdminMarkets = lazy(() => import("./pages/app/admin-markets"));
const AppAdminTemplates = lazy(() => import("./pages/app/admin-templates"));
const AppAdminFlowMeshJobs = lazy(() => import("./pages/app/admin-flowmesh-jobs"));
const AdminSectionLayout = lazy(() => import("./pages/app/admin-section-layout"));

function LoginPage() {
  const { login } = useAuth();
  return (
    <Login
      onLogin={(token, user) => {
        login(token, user);
      }}
    />
  );
}

function RegisterPage() {
  const navigate = useNavigate();
  return (
    <Register
      // Keep ?return_to across login <-> register: a new person who arrived from an
      // OAuth client (e.g. runmesh.ai) must land back there after signing up.
      onSwitchToLogin={() => navigate(`/auth/login${window.location.search}`)}
      onRegisterSuccess={() => navigate(`/auth/login${window.location.search}`)}
    />
  );
}

// /auth/account/<tail>, /auth/dashboard/<tail>, /account/<tail>, and
// /app/<tail> all rewrite to /dashboard/<tail> so every stale bookmark
// keeps working after the 2026-04-24 merge.
function LegacyDashboardRedirect() {
  const { "*": tail = "" } = useParams();
  const loc = useLocation();
  const dest = tail ? `/dashboard/${tail}${loc.search}` : `/dashboard${loc.search}`;
  return <Navigate to={dest} replace />;
}

// /app/admin/* → /dashboard/admin/<rest>. The old /app shell's admin tree
// moved to /dashboard/admin (e.g. /dashboard/admin/clusters/<id>); old
// links like /app/admin/clusters/<id> need to land on the cluster detail
// page, not the studio catch-all. Preserves path tail + query.
// Legacy /dashboard/account/connect/google → Studio, PRESERVING the query
// string. A bare <Navigate to="/studio/..."> would drop ?return_to=/?then=,
// silently losing the OAuth return address (onboarding + /go both rely on it).
function ConnectGoogleStudioRedirect() {
  const loc = useLocation();
  return <Navigate to={`/studio/account/connect/google${loc.search}`} replace />;
}
function AppAdminRedirect() {
  const { "*": tail = "" } = useParams();
  const loc = useLocation();
  const dest = tail ? `/studio/admin/${tail}${loc.search}` : `/studio/admin${loc.search}`;
  return <Navigate to={dest} replace />;
}

// Opening an app (its surface at /studio/a/:app, or its overview at
// /studio/apps/:app) now drops you INTO the chat with the app rendered inline
// + the agent grounded on it — the deeper migration into conversation. The
// dense standalone page survives as the "?full=1" escape hatch. We stash the
// app synchronously (before <Navigate> fires) so StudioChat consumes it on
// mount; the full page renders in place when ?full=1.
// Bare app routes (/studio/a/:app, and gpu-rentals home) now open the app
// WORKSPACE (/studio/apps/:app) — the morphing 3-panel view (nav · details ·
// grounded chat). ?full=1 stays the standalone surface escape hatch.
function OpenAppRedirect({ app: appProp, surface }: { app?: string; surface?: string }) {
  const { app: paramApp = "" } = useParams();
  const app = appProp ?? paramApp;
  const [sp] = useSearchParams();
  if (sp.get("full") === "1") {
    return appProp ? <AppSurface app={appProp} surface={surface} /> : <AppSurface />;
  }
  return <Navigate to={`/studio/apps/${encodeURIComponent(app)}`} replace />;
}

// /dashboard/admin/* → /studio/admin/*. The whole /dashboard URL surface
// was retired 2026-06-19 (everything consolidated under /studio); this maps
// the deep admin tail so links like /dashboard/admin/users/matrix survive as
// /studio/admin/users/matrix, query string preserved.
function DashboardAdminToStudio() {
  const tail = useParams()["*"] ?? "";
  const loc = useLocation();
  return <Navigate to={`/studio/admin/${tail}${loc.search}`} replace />;
}

// Route RENAMES 2026-09-13 (operator request): the nav labels became
// "Data Warehouse" and "Research Fleet", and the paths now match them so label,
// route, heading and destination all agree. These two keep every existing link,
// bookmark and chat citation working — splat + query preserved, same shape as
// DashboardAdminToStudio above.
//
// Do NOT delete them on the assumption "nothing links there any more": the old
// paths were the canonical ones for months, they appear in saved chat threads and
// in e2e scripts, and /studio/compute was itself already the redirect TARGET for
// ~8 older paths (/studio/admin/clusters, fm/nodes, fm/workers, ssh, sites, …).
// Those now point straight at /studio/research-fleet, so this is a safety net for
// external links rather than an internal hop.
function ComputeToResearchFleetRedirect() {
  const tail = useParams()["*"] ?? "";
  const loc = useLocation();
  return <Navigate to={`/studio/research-fleet${tail ? `/${tail}` : ""}${loc.search}`} replace />;
}

function DataToDataWarehouseRedirect() {
  const loc = useLocation();
  return <Navigate to={`/studio/data-warehouse${loc.search}`} replace />;
}

// Phase 4 (app→agent): /studio/agents/:app → /studio/apps/:app, preserving the
// query (e.g. ?selected=<loop>). Forward-compat for the canonical "agents"
// surface while internal navigate() targets still point at /apps.
function AgentsToAppsRedirect() {
  const { app = "" } = useParams();
  const loc = useLocation();
  return <Navigate to={`/studio/apps/${encodeURIComponent(app)}${loc.search}`} replace />;
}

// /studio/workflows → /studio/apps, PRESERVING the query. ?compose=1 must
// reach the composer host, which is /studio/apps/all (apps.tsx reads it);
// /studio/apps is StudioWorkspace, which self-redirects and drops the query
// (see InboxZeroState in pages/studio/inbox.tsx).
function WorkflowsListRedirect() {
  const loc = useLocation();
  const compose = new URLSearchParams(loc.search).get("compose") === "1";
  return <Navigate to={`/studio/apps${compose ? "/all" : ""}${loc.search}`} replace />;
}

// /studio/workflows/:slug (slug = "<app>:<loop>") → the workflow page.
function WorkflowSlugRedirect() {
  const { slug = "" } = useParams();
  return <Navigate to={legacyWorkflowSlugTarget(slug)} replace />;
}

// The standalone cycle inspector became the run page. Old
// /studio/(intents|today)/cycle/:app/:loop/:ts deep links land on that run.
function CycleRedirect() {
  const { app = "", loop = "", ts = "" } = useParams();
  return <Navigate to={legacyCycleTarget(app, loop, ts)} replace />;
}

// Redirect dashboard quant/* routes to their Studio equivalents.
// Pattern may contain :paramName tokens that are replaced with matched params.
function ParamRedirect({ pattern }: { pattern: string }) {
  const params = useParams();
  const to = pattern.replace(/:(\w+)/g, (_, k) => params[k] ?? "");
  return <Navigate to={to} replace />;
}

// Cold-load fallback — the shared on-brand spiral loader on contextual gray boxes.
function Spinner() {
  return <BrandLoader />;
}

// RoleHome — used at "/" and the catch-all "*". Reads auth + role and
// Navigates to the right surface. Without this, "/" would render
// <Navigate to="/auth/login">, and the AuthGuard on /auth/login would
// then bounce already-authed users to /dashboard — landing every
// regular user on the admin shell even though they wanted /app.
function RoleHome({ publicLanding = false }: { publicLanding?: boolean }) {
  const { isLoading, isAuthenticated, user } = useAuth();
  if (isLoading) return <Spinner />;
  // Bare "/" shows a logged-out visitor what Lumid is (GOALS.md §6.14, J1);
  // every other unmatched path keeps bouncing to the login form.
  if (!isAuthenticated) return publicLanding ? <Landing /> : <Navigate to="/auth/login" replace />;
  return <Navigate to={defaultLandingPath(user?.role)} replace />;
}

// RootEntry — root "/" branches by build-time bundle. The /go bundle
// (xp.io/go/*) renders the composer directly; lum.id (no basename)
// keeps the existing role-aware redirect. Inlined env constant so
// Vite tree-shakes the unused branch from each bundle.
const IS_GO_BUNDLE =
  (import.meta.env.VITE_ROUTER_BASE_PATH || "").replace(/\/$/, "") === "/go";
function RootEntry({ publicLanding = false }: { publicLanding?: boolean }) {
  if (IS_GO_BUNDLE) return <Go />;
  return <RoleHome publicLanding={publicLanding} />;
}

// Deploy watcher — a long-lived SPA tab never refetches index.html, so after a
// UI push the user keeps running the old bundle (and clicking through "fixed"
// bugs). Poll the shell's ETag (no-cache on index.html makes this exact) every
// few minutes + on tab focus; when it changes, prompt one reload. Also reload
// automatically when a lazy route chunk 404s (its hashed file was replaced by
// the deploy) — Vite signals that via the `vite:preloadError` event.
// reloadForStaleChunk — hard-reload once to pick up the new bundle when a
// stale chunk fails to load. Guarded so a genuinely-broken chunk (not just
// stale) can't spin in a reload loop: at most one reload per 15s.
function reloadForStaleChunk() {
  try {
    const k = "chunk_reload_at";
    const last = Number(sessionStorage.getItem(k) || 0);
    if (Date.now() - last < 15_000) return; // already reloaded recently — avoid loop
    sessionStorage.setItem(k, String(Date.now()));
  } catch { /* sessionStorage blocked — still try one reload */ }
  window.location.reload();
}

function useDeployWatch() {
  useEffect(() => {
    let etag: string | null = null;
    let prompted = false;
    const check = async () => {
      try {
        const r = await fetch("/studio/", { method: "HEAD", cache: "no-store" });
        const e = r.headers.get("etag");
        if (!e) return;
        if (etag === null) { etag = e; return; }
        if (e !== etag && !prompted) {
          prompted = true;
          toast.info("A new version of Lumid is available.", {
            duration: Infinity,
            action: { label: "Reload", onClick: () => window.location.reload() },
          });
        }
      } catch { /* offline / transient — ignore */ }
    };
    check();
    const id = setInterval(check, 5 * 60_000);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    const onPreloadError = (e: Event) => { e.preventDefault(); reloadForStaleChunk(); };
    window.addEventListener("vite:preloadError", onPreloadError);
    // A click-time dynamic import() of a stale (post-deploy) chunk rejects as a
    // plain error React.lazy surfaces — NOT always a vite:preloadError — which
    // white-screens the route ("library doesn't render"). Catch those globally
    // and reload once (guarded against reload loops) so the route self-heals.
    const isChunkErr = (msg: string) =>
      /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|module script failed/i.test(msg);
    const onRejection = (e: PromiseRejectionEvent) => {
      const msg = String(e?.reason?.message || e?.reason || "");
      if (isChunkErr(msg)) reloadForStaleChunk();
    };
    const onError = (e: ErrorEvent) => {
      if (isChunkErr(String(e?.message || ""))) reloadForStaleChunk();
    };
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onError);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("vite:preloadError", onPreloadError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onError);
    };
  }, []);
}

export default function App() {
  useDeployWatch();
  return (
    <AuthProvider>
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* Auth flows — unchanged under /auth/*. */}
          <Route
            path="/auth/login"
            element={
              <AuthGuard requireAuth={false}>
                <LoginPage />
              </AuthGuard>
            }
          />
          <Route
            path="/auth/register"
            element={
              <AuthGuard requireAuth={false}>
                <RegisterPage />
              </AuthGuard>
            }
          />
          {/* /auth/signup is the name people (and the landing) use for sign-up.
              It used to fall through to the "*" catch-all, which sent a
              logged-out visitor to the LOGIN form. Same page, same guard as
              /auth/register — no redirect, so ?code=<invite> survives. */}
          <Route
            path="/auth/signup"
            element={
              <AuthGuard requireAuth={false}>
                <RegisterPage />
              </AuthGuard>
            }
          />
          <Route path="/auth/callback" element={<Callback />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          {/* Public docs — anyone browsing app repos before forking
              should be able to read the canonical xpio contract. */}
          <Route path="/docs/xpio-autoresearch" element={<XpioAutoresearchDoc />} />
          <Route path="/docs/quant-quickstart" element={<QuantQuickstartDoc />} />
          {/* Internal ops runbook (GHCR repos, deploy topology) — AUTH REQUIRED,
              unlike the public xpio contract above. */}
          <Route
            path="/docs/plugin-image-cd"
            element={
              <AuthGuard requireAuth={true}>
                <PluginImageCdDoc />
              </AuthGuard>
            }
          />
          {/* End-user "write & submit a strategy" guide — AUTH REQUIRED
              (matches plugin-image-cd; submitting needs an account + PAT). */}
          <Route
            path="/docs/lqt-strategies"
            element={
              <AuthGuard requireAuth={true}>
                <LqtStrategiesDoc />
              </AuthGuard>
            }
          />
          {/* Claude account-pool user guide — AUTH REQUIRED (org-internal;
              using the pool needs an account + claude:proxy PAT anyway). */}
          <Route
            path="/docs/claude"
            element={
              <AuthGuard requireAuth={true}>
                <ClaudePoolDoc />
              </AuthGuard>
            }
          />
          {/* Operations checklist — AUTH REQUIRED (internal ops runbook,
              matches plugin-image-cd). Companion to the live status page
              at /status/operations. */}
          <Route
            path="/docs/operations"
            element={
              <AuthGuard requireAuth={true}>
                <OperationsDoc />
              </AuthGuard>
            }
          />

          {/* /status → /status/operations shorthand. */}
          <Route path="/status" element={<Navigate to="/status/operations" replace />} />
          {/* Live operations status — ADMIN AND ABOVE (widened from super_admin
              2026-09-11). Standalone page (own layout), so it lives at the top
              level rather than inside the studio shell. Companion doc at
              /docs/operations.

              THIS GUARD IS UX ONLY — the real gate is the nginx auth_request on
              `location /status` in deploy_infra/k8s-lift/nginx/lum-id-landing.conf.
              Until 2026-09-11 that block had no auth_request at all, so the page
              answered HTTP 200 to an anonymous fetch while this guard made it
              *look* restricted: the SPA shell and every asset were public, and
              only the rendered view was withheld. If you change who may see this
              page, change BOTH layers or you have changed nothing. */}
          <Route
            path="/status/operations"
            element={
              <AdminGuard>
                <OperationsStatusPage />
              </AdminGuard>
            }
          />
          {/* Claude Code quota dashboard — admin + super_admin, top-level /code */}
          <Route
            path="/code"
            element={
              <AdminGuard>
                <StudioClaudeQuota />
              </AdminGuard>
            }
          />
          {/* Claude pool session transcripts — any authed pool user sees their
              own; super_admin can flip to the all-users view (gated server-side). */}
          <Route
            path="/claude-sessions"
            element={
              <AuthGuard requireAuth={true}>
                <StudioClaudeSessions />
              </AuthGuard>
            }
          />

          {/* Public marketplace (anonymous discovery) — replaces the retired
              xp.io SPA. Browse + read-only repo pages without login; install/
              subscribe bounce through the authed studio (login prompt). */}
          <Route path="/explore" element={<PublicShell />}>
            <Route index element={<StudioMarketplace />} />
            <Route path="r/:owner/:name" element={<StudioRepo />} />
          </Route>

          {/* === Web-first revamp (2026-05-22) ===========================
              New user-facing surface mounted under /app/*. Same React
              bundle is also built with VITE_ROUTER_BASE_PATH=/go for the
              xp.io/go/* staging deploy — those URLs resolve here too
              because BrowserRouter basename strips the prefix at runtime.
              Replaces /dashboard/{marketplace,loops,knowledge,results}
              for the user persona; admins keep the /dashboard/* shell. */}
          <Route
            path="/onboarding/welcome"
            element={
              <AuthGuard requireAuth={true}>
                <OnboardingWelcome />
              </AuthGuard>
            }
          />
          <Route
            path="/onboarding/domain"
            element={
              <AuthGuard requireAuth={true}>
                <OnboardingDomain />
              </AuthGuard>
            }
          />
          <Route
            path="/onboarding/ready"
            element={
              <AuthGuard requireAuth={true}>
                <OnboardingReady />
              </AuthGuard>
            }
          />
          {/* /app/* shell removed in Phase S7 cutover — see the
              <Navigate /> redirects further down. The old UserLayout
              + AppHome/AppMarketplace/etc. pages are no longer
              mounted; the Studio shell handles all user surfaces. */}

          {/* Lumid Studio — Phase S1 unified shell. Lives alongside
              /dashboard (admin) and replaced /app entirely as of S7.
              The shell renders the left nav + top bar; each child
              route fills the workspace area. */}
          <Route
            path="/studio"
            element={
              <AuthGuard requireAuth={true}>
                <StudioShell />
              </AuthGuard>
            }
          >
            {/* The main page stays the chat home (2-panel: sidebar + chat). The
                3-panel workspace is ONLY for app pages (/studio/apps/:app). */}
            <Route index             element={<StudioChatHome />} />
            {/* A saved thread by URL. Thread identity was localStorage-only,
                so a conversation had no address: /studio/chat/<id> links from
                other surfaces (the per-strategy Sessions table emits exactly
                this href) hit the catch-all below, and two threads could not
                be open at once. Same surface, one thread pre-opened. */}
            <Route path="chat/:id"   element={<StudioChatHome />} />
            {/* Spine is now My Apps. The old Intents/Today landings redirect
                there; their cycle-inspector + intent-detail sub-routes stay. */}
            <Route path="intents"                       element={<Navigate to="/studio/apps" replace />} />
            <Route path="today"                         element={<Navigate to="/studio/apps" replace />} />
            {/* Cycle inspector merged into the app-overview panel (per-stage
                content + cycle stepper). Old deep links redirect there. */}
            <Route path="intents/cycle/:app/:loop/:ts"  element={<CycleRedirect />} />
            <Route path="today/cycle/:app/:loop/:ts"    element={<CycleRedirect />} />
            {/* T13 — per-intent detail panel; dispatched by intent.body.kind. */}
            <Route path="intents/:intentId"             element={<StudioIntentDetail />} />
            {/* Inbox = the canonical xpcloud message inbox (cycle digests +
                AI-unsure "question" escalations + draft_pending), with the
                "Needs you" section. Was the drafts/audit feed (StudioInbox),
                which doesn't fetch xpcloud messages — so operators never saw
                posted questions. The drafts/audit feed lives at /studio/drafts. */}
            {/* Compute — the fleet surface, deliberately OUTSIDE /studio/admin.
                The home fleet is readable by ANY signed-in user; office, cloud,
                vast and NUS are admin+. That cannot be expressed under
                /studio/admin, whose whole subtree is inside <AdminGuard>.

                Access is enforced in three places and they are not redundant:
                  1. the tab strip hides what you cannot use (UX only);
                  2. <AdminGuard> on each restricted child, so a DEEP LINK
                     redirects instead of rendering an empty page;
                  3. the edge — nginx auth_request on /fm/api/v1/ plus
                     mesh-federator's per-site identity scoping — which is the
                     only one that actually protects the data.
                Fleet therefore asks for different URLs per role: see
                listNodesForSite() in src/api/fm.ts.

                SSH and Submit are admin+ even though the home fleet is public.
                "Usable by anyone" was scoped by the later, more specific
                instruction that Submit, Setup guide and Vast are admin+, and a
                shell on a GPU box is a bigger grant than reading its status. */}
            <Route
              path="research-fleet"
              element={
                <ComputeSection
                  title="Research Fleet"
                  subtitle="The federated GPU fleet behind lum.id/fm — sites, nodes and workers in one tree, plus jobs, shells and rented capacity."
                  // TWO tabs, down from five, because three of the five were not
                  // places:
                  //   Vast is a SITE — Fleet already lists its nodes and workers
                  //     with every other site, so the tab re-listed the same rows
                  //     and added only the rental economics, which now render on
                  //     the vast site itself.
                  //   SSH sessions are TASKS (task_type "ssh") — the same dataset
                  //     Jobs already fans out, so SSH is a view, not a page.
                  //   Submit is an ACTION — a tab you entered to do one thing and
                  //     left. It is a button on Jobs that opens a dialog.
                  // Their routes are kept as redirects below.
                  tabs={[
                    // "Nodes & workers", not "Fleet": the page is already titled Research Fleet.
                    { to: "/studio/research-fleet", label: "Nodes & workers", end: true, icon: Boxes },
                    // NOT requireAdmin. Access here is per worker/node, not per
                    // surface: a user sees their own jobs and their own SSH
                    // sessions on the fleet they can use, an admin sees all of it.
                    // FlowMesh scopes rows by identity, so this needs no gate of
                    // its own — hiding the tab would only hide a user's own work
                    // from them.
                    { to: "/studio/research-fleet/jobs", label: "Jobs", icon: ListChecks },
                    // Not requireAdmin: home is the fleet anyone may use, and
                    // sandbox-control scopes everything to the caller (own
                    // namespace, own quota, own keys, own home).
                    { to: "/studio/research-fleet/sandboxes", label: "Sandboxes", icon: TerminalSquare },
                    // Billing is NOT a tab here. It is account/finance, not fleet
                    // operations, and it was the only super_admin entry in a strip
                    // whose other tabs are admin+ — so for most admins it rendered a
                    // gap. It now lives in the user menu (StudioShell), beside
                    // Management and Claude pool. The route is unchanged.
                  ]}
                />
              }
            >
              <Route index element={<FmFleetRoute />} />
              <Route path="jobs" element={<FmJobsRoute />} />
              <Route path="sandboxes" element={<FmSandboxesRoute />} />
              {/* Absorbed into Fleet (vast) and Jobs (ssh, submit) on 2026-09-11.
                  Kept as redirects — these were linked from the tab strip and are
                  in people's history. */}
              <Route path="ssh" element={<Navigate to="/studio/research-fleet/jobs" replace />} />
              <Route path="submit" element={<Navigate to="/studio/research-fleet/jobs" replace />} />
              <Route path="vast" element={<Navigate to="/studio/research-fleet" replace />} />
              {/* The three tabs Fleet replaced. Kept as redirects, not deleted:
                  they were linked from the admin strip for months. */}
              <Route path="sites" element={<Navigate to="/studio/research-fleet" replace />} />
              <Route path="nodes" element={<Navigate to="/studio/research-fleet" replace />} />
              <Route path="workers" element={<Navigate to="/studio/research-fleet" replace />} />
            </Route>

            {/* The workflow editor. Until now every pages/app/workflow-* page was
                lazy-DECLARED and never rendered — AppWorkflows, AppWorkflowYaml,
                AppWorkflowDetail, AppWorkflowBuilder and AppN8n each appeared
                exactly once in this file, as an import — so the editor existed
                and nothing could open it. Three live navigate() calls pointed at
                /app/n8n, a route that was never registered either. */}
            <Route path="workflows/new"                element={<AppWorkflowYaml />} />
            {/* Your OWN saved workflows, with their state, and the one place they
                can be run. The editor used to save and navigate to /dashboard (a
                redirect to /studio), and the only listing (/studio/workflows/market) is the
                PUBLISHED market — so a saved draft appeared nowhere (FLB-QR-05). */}
            <Route path="workflows/mine"               element={<AppRunmeshSubmit />} />
            {/* The PUBLISHED workflow market (Runmesh templates to import). It was
                mounted at the bare `workflows` path, which was ALSO declared
                below as WorkflowsListRedirect — two routes for one path, so
                whichever was declared first silently won and the other was dead
                code. For role=user the apps page is the one place workflows
                live (2026-09-28), so bare /studio/workflows is the redirect and
                the market keeps its own distinct address here. Static
                `workflows/market` outranks `workflows/:slug`. */}
            <Route path="workflows/market"             element={<AppWorkflows />} />

            <Route path="inbox"                        element={<Inbox />} />
            <Route path="drafts"                       element={<StudioInbox />} />
            <Route path="account/inbox"                element={<Inbox />} />
            {/* lumid-market competition section — now PURE config surfaces.
                Each route mounts AppSurface with an explicit named surface
                (declared in the bundle's xpcloud.yaml ui.surfaces); the page
                content, links, and clickables are generated from ui/*.md.
                URL params (competitionId, strategyId) are injected into the
                directive sources. Static segments beat :competitionId, which
                beats the generic :app/:surface below. The few irreducibly
                interactive widgets (sortable leaderboard, live feed, AI wizard,
                strategy inspector) are lumid:native embeds inside those .md
                files — see quantarena/surface-embeds.tsx. */}
            <Route path="a/lumid-market/competition"          element={<AppSurface app="lumid-market" surface="lobby" />} />
            <Route path="a/lumid-market/competition/lobby"    element={<AppSurface app="lumid-market" surface="lobby" />} />
            <Route path="a/lumid-market/competition/my"       element={<AppSurface app="lumid-market" surface="my-strategies" />} />
            <Route path="a/lumid-market/competition/pathways" element={<AppSurface app="lumid-market" surface="pathways" />} />
            <Route path="a/lumid-market/competition/:competitionId"                        element={<AppSurface app="lumid-market" surface="competition-detail" />} />
            <Route path="a/lumid-market/competition/:competitionId/strategy/:strategyId"   element={<AppSurface app="lumid-market" surface="strategy-detail" />} />
            <Route path="a/lumid-market/strategy/research/:strategyId"                     element={<AppSurface app="lumid-market" surface="research" />} />
            {/* lumid-gpu-rentals — PURE config surfaces (same pattern as
                lumid-market). home = pricing + your rentals (config);
                new = create form (config); detail = the irreducibly-interactive
                terminal/SSH/logs/billing as a lumid:native escape-hatch. The
                :id param is the FlowMesh task_id, injected into the detail
                embed.
                ROUTE-RANK TRAP (2026-06-11): `a/lumid-gpu-rentals/:id` scores
                10+3 — it BEATS the generic `a/:app/:surface` (3+3) and TIES
                `a/:app/manage` (3+10), so /home and /manage rendered the
                rental-detail page with id="home"/"manage". Every reserved
                name needs an explicit static route here (10+10 wins all),
                and this block is declared BEFORE the editor/manage routes
                only for the bare + static paths — :id ties resolve in favor
                of whichever is declared FIRST, so keep `:id` LAST in the
                whole a/* group (it lives below, after manage/config/edit). */}
            {/* Home opens in chat (surface card + grounded agent), like every
                other app. ?full=1 = the standalone page. /new + /:id stay full. */}
            <Route path="a/lumid-gpu-rentals"           element={<OpenAppRedirect app="lumid-gpu-rentals" surface="home" />} />
            <Route path="a/lumid-gpu-rentals/home"      element={<OpenAppRedirect app="lumid-gpu-rentals" surface="home" />} />
            <Route path="a/lumid-gpu-rentals/new"       element={<AppSurface app="lumid-gpu-rentals" surface="new" />} />
            {/* lumid-data-findata per-symbol drill-down: a row in Movers/Earnings/
                IPOs links here; {symbol} is injected into the detail surface
                (ohlc price chart + fundamentals + news). Beats the generic route. */}
            <Route path="a/lumid-data-findata/symbol/:symbol" element={<AppSurface app="lumid-data-findata" surface="symbol" />} />
            {/* App-defined UI surface — apps declare ui.surface in xpcloud.yaml;
                served as runtime markdown (or a first-party native key). */}
            {/* Editor/config routes must come before the generic :surface param
                route so React Router v7's static-segment scoring resolves correctly. */}
            <Route path="a/:app/edit"                   element={<AppSurfaceEditor />} />
            <Route path="a/:app/edit/:surface"          element={<AppSurfaceEditor />} />
            <Route path="a/:app/config"                 element={<AppConfigEditor />} />
            {/* Tune › Prompts (WS-7) — edit the analyst & judge prompt files.
                Static segment beats the generic :surface below. */}
            <Route path="a/:app/prompts"                element={<AppPromptsEditor />} />
            {/* App management — rename (card/sidebar label), workflows
                (create/run/remove loops), skill imports. Static segment
                beats the generic :surface below. */}
            <Route path="a/:app/manage"                 element={<AppManagePanel />} />
            {/* gpu-rentals rental detail — :id is the FlowMesh task_id. Declared
                AFTER manage/config/edit so those static names win their rank
                ties; still beats the generic :app/:surface for real ids. */}
            <Route path="a/lumid-gpu-rentals/:id"       element={<AppSurface app="lumid-gpu-rentals" surface="detail" />} />
            {/* Bare app surface → open in chat (deeper migration). ?full=1 =
                the standalone page. Named sub-surfaces stay full-page. */}
            <Route path="a/:app"                        element={<OpenAppRedirect />} />
            <Route path="a/:app/:surface"               element={<AppSurface />} />
            {/* Account surfaces folded into the one Studio shell. The old
                /dashboard/{profile,tokens,account/connect/google} routes stay
                reachable (back-compat) and redirect here. */}
            <Route path="account/profile"               element={<Profile />} />
            <Route path="account/tokens"                element={<Tokens />} />
            {/* Delegated Claude-pool management. NO guard beyond /studio's
                AuthGuard, deliberately: the capability is per (pool, user) and
                lives server-side, so the page renders its own empty state for
                the overwhelming majority who manage nothing. A role guard here
                could only approximate it, and would be wrong in both
                directions. */}
            <Route path="account/claude-pool"           element={<ClaudePoolManage />} />
            <Route path="account/findata-sql"           element={<FindataSQL />} />
            <Route path="account/connect/google"        element={<ConnectGoogle />} />
            {/* /studio/manage = AdminOverview (cluster/users/audit operational hub).
                Claude Code quota lives at the top-level /code route (admin+).
                The sidebar's "Management" entry is admin-only, but "manage" is
                the word a role=user reaches for (and old links carry), and the
                default AdminGuard fallback bounced them to the chat home — which
                read as a broken page. A non-admin lands on the thing they CAN
                manage: their apps ("My apps & runs", /studio/apps/all). */}
            <Route path="manage" element={<AdminGuard fallback="/studio/apps/all"><AdminOverview /></AdminGuard>} />
            <Route path="admin-overview" element={<AdminGuard><AdminOverview /></AdminGuard>} />
            <Route path="admin/apps/:app/insights" element={<AdminGuard><AppInsights /></AdminGuard>} />
            {/* Sidebar consolidation 2026-05-25: skills merged into the
                catalog (now "Library"); runs + mind folded into Workflows.
                Marketplace → Library rename (demo IA); old paths redirect.
                The StudioApps editor still mounts under /apps/:app to handle
                per-app YAML edits until the dedicated workflow editor ships. */}
            {/* Library page removed → the marketplace lives on xp.io. */}
            {/* In-Studio marketplace — browse + install xpio apps/skills/datasets
                (MarketplaceBrowse). Was redirecting out to xp.io, leaving no
                in-app install affordance; now mounts the page so install works
                from the dashboard. */}
            {/* Data — a system app (Catalog + Explorer tabs), like Library.
                Catalog is the federated data-lake viewer; Explorer runs any
                allowlisted data-app's declared endpoints. Renders the native
                components directly — no /me/apps/:app/ui round-trip. */}
            <Route path="data-warehouse"               element={<StudioData />} />
            {/* Old paths, renamed 2026-09-13. Splat-preserving so
                /studio/compute/jobs and /studio/compute/sandboxes survive. */}
            <Route path="compute/*"                    element={<ComputeToResearchFleetRedirect />} />
            <Route path="compute"                      element={<ComputeToResearchFleetRedirect />} />
            <Route path="data"                         element={<DataToDataWarehouseRedirect />} />
            {/* Library — Marketplace / Skills / Experiments as tabs under
                one nav entry; the old top-level paths redirect in. */}
            <Route path="library"                      element={<StudioLibraryTabs />}>
              <Route index                             element={<Navigate to="marketplace" replace />} />
              <Route path="marketplace"                element={<StudioMarketplace />} />
              <Route path="skills"                     element={<StudioSkills />} />
              <Route path="experiments"                element={<StudioExperiments />} />
            </Route>
            {/* Read-only repo browser (files + PRs) — replaces the xp_ui repo
                page. Same component serves the public /explore/r/:owner/:name. */}
            <Route path="r/:owner/:name"               element={<StudioRepo />} />
            <Route path="marketplace"                  element={<Navigate to="/studio/library/marketplace" replace />} />
            <Route path="skills"                       element={<Navigate to="/studio/library/skills" replace />} />
            <Route path="experiments"                  element={<Navigate to="/studio/library/experiments" replace />} />
            {/* Knowledge is now the "you, encoded" ledger; the per-agent
                bank browser keeps its deep-link at /knowledge/:agent. */}
            <Route path="knowledge"                    element={<StudioKnowledgeEncoded />} />
            <Route path="knowledge/:agent"             element={<StudioKnowledge />} />
            {/* The Apps experience is the workspace (one featured app + switcher
                + grounded chat), NOT a grid. /apps opens the last-featured app;
                /apps/all is the full installed-app inventory (reached from the
                user menu's "Manage apps"). */}
            <Route path="apps"                         element={<StudioWorkspace />} />
            <Route path="apps/all"                     element={<StudioApps />} />
            <Route path="apps/:app"                    element={<StudioWorkspace />} />
            {/* A workflow and one of its runs are ADDRESSES (lib/run-routes.ts):
                the run page, the expanded step and its pane all live in the URL,
                so a run can be linked and survives a reload. Same workspace
                element, so the docked chat stays mounted across them. */}
            <Route path="apps/:app/w/:loop"            element={<StudioWorkspace />} />
            <Route path="apps/:app/w/:loop/r/:runId"   element={<StudioWorkspace />} />
            {/* Phase 4 (app→agent, docs/architecture/unified-components.md):
                the canonical surface is "agents". Forward-compat aliases route
                /studio/agents* → the existing /studio/apps* views so canonical
                links work today; the existing navigate() targets are migrated
                incrementally. (Reverse /apps→/agents 301 is the eventual end
                state once all internal links use /agents.) */}
            <Route path="agents"                       element={<Navigate to="/studio/apps" replace />} />
            <Route path="agents/all"                   element={<Navigate to="/studio/apps/all" replace />} />
            <Route path="agents/:app"                  element={<AgentsToAppsRedirect />} />
            {/* Workflows folded into the per-app overview; list redirects,
                detail pages stay reachable via deep-link. The only `workflows`
                list route — the market moved to workflows/market above. */}
            <Route path="workflows"                    element={<WorkflowsListRedirect />} />
            <Route path="workflows/:slug"              element={<WorkflowSlugRedirect />} />
            {/* Runs + Mind kept reachable for back-compat and direct
                links from chat tools. Their lens-in-Workflows ports
                land in a follow-up PR; both still work at their
                original URLs and are just hidden from the sidebar. */}
            <Route path="runs"                         element={<StudioRuns />} />
            {/* Fleet had TWO addresses for one view. apps.tsx imports this very
                component and renders <StudioPortfolio embedded /> as its "Fleet
                rollup", and the sidebar already documents Fleet as "merged into
                Manage apps — no separate entry" — but the standalone route stayed
                mounted, so the merge was only half done and the same table lived at
                two URLs. Nothing in the nav linked here; the redirect finishes it.
                The component itself is KEPT and still does the work, embedded. */}
            <Route path="portfolio"                    element={<Navigate to="/studio/apps/all" replace />} />
            <Route path="strategies"                   element={<StudioStrategies />} />
            <Route path="runs/:run_id"                 element={<StudioRunDetail />} />
            {/* Mind folded into each workflow's Insights panel. */}
            <Route path="mind"                         element={<Navigate to="/studio/apps" replace />} />
            {/* "How it works" folded into the Documentation panel — old
                deep links redirect. */}
            <Route path="how"                          element={<Navigate to="/studio/docs/how" replace />} />
            {/* Documentation panel — index over every guide/runbook/contract,
                with an in-shell reader. Standalone /docs/* routes stay live
                (public xpio contract + external deep links). */}
            <Route path="docs"                         element={<StudioDocs />} />
            <Route path="docs/:slug"                   element={<StudioDocs />} />
            <Route path="settings"                     element={<StudioSettings />} />
            {/* Billing — folded into Studio account (was /dashboard/billing). */}
            <Route path="account/billing"              element={<AppBilling />} />
            {/* Admin (Option B) — lean StudioAdmin is the landing; the deep
                admin pages from the retired /dashboard/admin tree are re-homed
                here under /studio/admin/*. Gated by AdminGuard. */}
            <Route
              path="admin"
              element={
                <AdminGuard>
                  <Outlet />
                </AdminGuard>
              }
            >
              <Route index element={<StudioAdmin />} />

              {/* People & access — 5 tabs */}
              <Route
                element={
                  <AdminSectionLayout
                    title="People & access"
                    subtitle="Users, roles, invitations, and the audit trail."
                    tabs={[
                      { to: "/studio/admin/users", label: "Users", end: true },
                      { to: "/studio/admin/users/matrix", label: "Access matrix" },
                      { to: "/studio/admin/invitations", label: "Invitations" },
                      { to: "/studio/admin/audit", label: "Audit log" },
                      { to: "/studio/admin/setup", label: "Setup" },
                    ]}
                  />
                }
              >
                <Route path="users" element={<AppAdminUsers />} />
                <Route path="users/matrix" element={<AppAdminUsersMatrix />} />
                <Route path="invitations" element={<AdminInvitations />} />
                <Route path="audit" element={<AppAdminAudit />} />
                <Route path="setup" element={<AppAdminSetup />} />
              </Route>
              {/* User detail lives outside the tab layout — it's drill-down, not peer. */}
              <Route path="users/:id" element={<AppAdminUserDetail />} />

              {/* Compute MOVED to /studio/compute (2026-09-11).
                  It left /studio/admin because that whole subtree is wrapped in
                  <AdminGuard>, and the home fleet has to be readable by any
                  signed-in user — a section cannot be partly public from inside a
                  blanket guard. These redirects keep every existing link, bookmark
                  and in-app reference working; do not delete them. */}
              <Route path="clusters" element={<Navigate to="/studio/research-fleet" replace />} />
              <Route path="fm/nodes" element={<Navigate to="/studio/research-fleet" replace />} />
              <Route path="fm/workers" element={<Navigate to="/studio/research-fleet" replace />} />
              <Route path="fm/jobs" element={<Navigate to="/studio/research-fleet/jobs" replace />} />
              <Route path="fm/submit" element={<Navigate to="/studio/research-fleet/submit" replace />} />
              <Route path="fm/vast" element={<Navigate to="/studio/research-fleet/vast" replace />} />
              {/* The Setup GUIDE moved to /studio/docs/infrastructure-setup (Admin+).
                  This route stays because the page is not only prose: it MINTS
                  cluster bootstrap tokens (cluster picker + TTL + the one-line
                  installer), which markdown cannot do. Redirecting it to the doc
                  would have deleted a working capability while looking like a
                  documentation move. The doc's step 3 links here. Off the nav by
                  design — you arrive from the doc, at the moment you need a token. */}
              <Route path="infra-setup" element={<AppAdminInfrastructureSetup />} />

              {/* Legacy lumid_cluster registry — kept, still admin-only, and still
                  OFF the navigation. The mirror it reads is retired
                  (fm-registry-sync suspended) and the live view is /studio/compute,
                  but these remain the only UI for things with live backend
                  dependents and no /fm equivalent:
                    - cluster_servers CRUD — claude-proxy's and claude-sandbox's
                      /fm/c/<id> path reads those rows for the per-cluster operator key
                    - cluster create/patch/delete, Runmesh vendor + commercial billing
                    - worker cost / selling_price_per_hour editing
                  Reachable by direct URL; delete only after those move or die. */}
              <Route
                element={
                  <AdminSectionLayout
                    title="Cluster registry"
                    subtitle="Legacy lumid_cluster admin. The live fleet view is Compute; these screens remain for cluster CRUD, bootstrap tokens and commercial fields, which have no /fm equivalent."
                  />
                }
              >
                <Route path="cluster-registry" element={<AppAdminClusters />} />
                <Route path="cluster-workers" element={<AppAdminClusterWorkers />} />
                <Route path="suppliers" element={<RunmeshSuppliers />} />
                <Route path="supplier-nodes" element={<RunmeshSupplierNodes />} />
                <Route path="workflow-review" element={<RunmeshWorkflowReview />} />
              </Route>
              {/* Billing keeps its /studio/admin URL — it is super_admin-only and has
                  no public half, so it has no reason to move. The Compute strip links
                  to it. */}
              <Route
                path="billing"
                element={
                  <SuperAdminGuard>
                    <RunmeshBilling />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="lumilake-workers"
                element={
                  <Navigate to="/studio/admin/cluster-workers?role=lumilake" replace />
                }
              />
              <Route path="clusters/new" element={<AppAdminClustersNew />} />
              {/* Cluster detail uses its own tab strip in place of the
                  Infrastructure section strip. */}
              <Route path="clusters/:id" element={<AppAdminClustersDetail />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<ClusterDetailOverview />} />
                <Route path="servers" element={<ClusterDetailServers />} />
                <Route path="nodes" element={<ClusterDetailNodes />} />
                <Route path="workers" element={<ClusterDetailWorkers />} />
                <Route path="commercial" element={<ClusterDetailCommercial />} />
                <Route path="submit" element={<ClusterDetailSubmit />} />
              </Route>

              {/* QuantArena — 4 tabs */}
              <Route
                element={
                  <AdminSectionLayout
                    title="QuantArena"
                    subtitle="Trading platform admin — competitions, markets, templates, jobs."
                    tabs={[
                      { to: "/studio/admin/competitions", label: "Competitions", end: true },
                      { to: "/studio/admin/markets", label: "Markets" },
                      { to: "/studio/admin/templates", label: "Backtest templates" },
                      { to: "/studio/admin/flowmesh-jobs", label: "FlowMesh jobs" },
                    ]}
                  />
                }
              >
                <Route path="competitions" element={<AppAdminCompetitions />} />
                <Route path="markets" element={<AppAdminMarkets />} />
                <Route path="templates" element={<AppAdminTemplates />} />
                <Route path="flowmesh-jobs" element={<AppAdminFlowMeshJobs />} />
              </Route>

              {/* Retired surface — legacy deep-link redirects */}
              <Route path="nodes" element={<Navigate to="/studio/admin/clusters" replace />} />
              <Route path="lumilake-users" element={<Navigate to="/studio/admin/users" replace />} />
            </Route>
            {/* Super-admin single pane of glass. */}
            <Route
              path="super-admin"
              element={
                <SuperAdminGuard>
                  <SuperAdminDashboard />
                </SuperAdminGuard>
              }
            />
          </Route>
          {/* Authenticated-but-incomplete users (empty invitation_code)
              get redirected here by AuthGuard. The page itself runs
              behind AuthGuard so unauth users still bounce to /login. */}
          <Route
            path="/auth/redeem-invite"
            element={
              <AuthGuard requireAuth={true}>
                <RedeemInvite />
              </AuthGuard>
            }
          />

          {/* ============================================================
              /dashboard URL surface RETIRED 2026-06-19. Everything is
              consolidated under /studio (both shells already rendered
              StudioShell). These are flat <Navigate> redirects — no shell —
              that preserve every old deep-link into its /studio equivalent.
              ============================================================ */}
          <Route path="/dashboard" element={<Navigate to="/studio" replace />} />
          <Route path="/dashboard/inbox" element={<Navigate to="/studio/inbox" replace />} />
          <Route path="/dashboard/marketplace" element={<Navigate to="/studio/library/marketplace" replace />} />
          <Route path="/dashboard/knowledge" element={<Navigate to="/studio/knowledge" replace />} />
          <Route path="/dashboard/results" element={<Navigate to="/studio/apps" replace />} />
          <Route path="/dashboard/loops" element={<Navigate to="/studio/runs" replace />} />
          <Route path="/dashboard/jobs" element={<Navigate to="/studio/apps" replace />} />
          <Route path="/dashboard/billing" element={<Navigate to="/studio/account/billing" replace />} />
          <Route path="/dashboard/profile" element={<Navigate to="/studio/account/profile" replace />} />
          <Route path="/dashboard/tokens" element={<Navigate to="/studio/account/tokens" replace />} />
          <Route path="/dashboard/auto-quant" element={<Navigate to="/studio/a/auto-quant" replace />} />
          <Route path="/dashboard/super-admin" element={<Navigate to="/studio/super-admin" replace />} />
          {/* OAuth connect — preserve ?return_to so the Google flow returns home. */}
          <Route path="/dashboard/account/connect/google" element={<ConnectGoogleStudioRedirect />} />
          {/* Deep admin tail → /studio/admin/<tail> (query preserved). */}
          <Route path="/dashboard/admin/*" element={<DashboardAdminToStudio />} />
          {/* Everything else under /dashboard → /studio. */}
          <Route path="/dashboard/*" element={<Navigate to="/studio" replace />} />

          {/* Legacy paths — one-hop redirect to /dashboard/*. */}
          <Route path="/auth/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/auth/dashboard/*" element={<LegacyDashboardRedirect />} />
          <Route path="/auth/account" element={<Navigate to="/dashboard" replace />} />
          <Route path="/auth/account/admin" element={<Navigate to="/dashboard" replace />} />
          <Route path="/auth/account/*" element={<LegacyDashboardRedirect />} />
          <Route path="/account" element={<Navigate to="/dashboard" replace />} />
          <Route path="/account/admin" element={<Navigate to="/dashboard" replace />} />
          <Route path="/account/*" element={<LegacyDashboardRedirect />} />
          {/* 2026-04-24 merge: /app/* was the product shell; now its
              entire tree lives under /dashboard/*. Catchall redirect
              preserves every deep link (sidebar history, Runmesh CLI
              output, docs, bookmarks). */}
          {/* /app/* — DEPRECATED as of Phase S7 cutover. The Research
              shell collapsed into Studio. All known /app/* deep links
              redirect to their Studio equivalent; unknown paths land
              on /studio/apps.

              The old LoopsPage / MarketplacePage / KnowledgePage /
              ResultsPage components remain imported because their
              contents are mounted under /studio/* (e.g. AppLoops is
              rendered inside StudioIntents). When inactive direct-mount
              references are pruned, those imports can go too.

              Old bookmarks → Studio mapping. These used to point at
              /studio/today, which has itself since been retired -- the
              `today` route right above now forwards to /studio/apps -- so
              the redirects go straight there rather than through a hop
              that only redirects again:
                /app             → /studio/apps
                /app/loops       → /studio/apps (AppLoops renders inside)
                /app/marketplace → xp.io (XpioRedirect, off-SPA)
                /app/knowledge   → /studio/knowledge
                /app/results     → /studio/apps */}
          <Route path="/app"             element={<Navigate to="/studio/apps"      replace />} />
          <Route path="/app/loops"       element={<Navigate to="/studio/apps"      replace />} />
          <Route path="/app/marketplace" element={<XpioRedirect />} />
          <Route path="/app/knowledge"   element={<Navigate to="/studio/knowledge" replace />} />
          <Route path="/app/results"     element={<Navigate to="/studio/apps"      replace />} />
          {/* /app/admin/* belonged to the old /app shell admin tree but
              everything admin now lives under /dashboard/admin/*. Preserve
              the rest of the path so deep links like
              /app/admin/clusters/<id> route through. */}
          <Route path="/app/admin/*"     element={<AppAdminRedirect />} />
          <Route path="/app/*"           element={<Navigate to="/studio/apps"      replace />} />

          {/* Root "/" — role-aware landing. AuthGuard(false) handles
              the unauth case by rendering <RoleHome>, which then reads
              user.role and Navigates to /app (user) or /dashboard
              (admin). Unauthed users fall to <RoleHome>'s unauth branch
              — on bare "/" that renders the public <Landing> (GOALS.md
              §6.14), on any other unmatched path it bounces to
              /auth/login. This replaces the previous
              two-hop /→/auth/login→/dashboard which would land regular
              users on the admin shell. */}
          {/* /go-composer — also reachable from the lum.id bundle so the
              landing CTA can deep-link. Same component renders here and
              at root of the /go bundle (see RootEntry above). */}
          <Route path="/go-composer" element={<Go />} />
          <Route path="/" element={<RootEntry publicLanding />} />
          <Route path="*" element={<RootEntry />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

